import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import {
  WORK_STATUS,
  N_OF_MEDIAN_TIME,
  INCLUDED_COUNTRIES_FOR_WORKSHIFT,
  EXCLUDED_DESIGNATION_FOR_WORKSHIFT,
} from 'constants/dashboard';
import { SELF } from 'constants/fetchTypes';
import { REMOTE } from 'constants/appConstants';
import { mixPanelEvents } from 'constants/mixPanel';
import { ATTENDANCE_URL } from 'constants/endpoints';

import * as mixpanel from 'services/mixPanel';

import { isObjectEmpty } from 'utils/object';
import { convertTimeToAMPM, getMedianTime } from 'utils/time';
import { subtractDays, getFormattedDate, getCurrentWeekRange, filterDataByDateRange } from 'utils/date';
import { handleError } from 'utils/errorHandler';

import useCurrentFiscalYear from 'hooks/useCurrentFiscalYear';
import { useCurrentUser } from 'hooks/query/useUserInfo';
import { useSelfAttendance } from 'contexts/SelfAttendanceContext';
import { useFetchAttendanceCalendarQuery, useFetchUnfreezeRequestsQuery } from 'hooks/query/useAttendanceQueries';
import { useFetchAttendanceSummary } from 'hooks/query/useFetchAttendanceSummary';

import ClockInTime from './components/ClockInTime';
import WorkStatusCount from './components/WorkStatusCount';
import { ClockInHistoryModal } from './components/clockInHistory/ClockInHistoryModal';
import { ClockInHistoryButton } from './components/clockInHistory/ClockInHistoryButton';
import { getWorkStatusSummaryData, getWorklogTotalDuration } from 'utils/leave';
import type { WorkStatusSummary } from 'interface/leave';

type User = {
  id?: string;
  country?: string;
  designation?: {
    name?: string;
  };
};

type FiscalYear = {
  startDate: string;
};

type CalendarDay = {
  date: string;
  checkinTime?: string | null;
};

type UnfreezeRequest = {
  date?: string;
  unfreezeFrom?: string;
  unfreezeTo?: string;
  updatedAt?: string;
  isUnfrozen?: boolean;
};

type UnfreezeRequestSummaryInput = {
  unfreezeFrom: string;
  unfreezeTo: string;
  updatedAt: string;
  isUnfrozen: boolean;
};

export default function AllocationCardFooter() {
  const lastHandledSummaryErrorAtRef = useRef(0);
  const user = useCurrentUser() as unknown as User;

  const shouldShowWorkshift =
    INCLUDED_COUNTRIES_FOR_WORKSHIFT.includes(user?.country) &&
    !EXCLUDED_DESIGNATION_FOR_WORKSHIFT.includes(user?.designation?.name);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const currentFiscalYear = useCurrentFiscalYear() as FiscalYear;

  const {
    selfAttendance: { missing: selfMissing, pending: selfPending },
    setSelfAttendance,
  } = useSelfAttendance();

  const { self } = mixPanelEvents.view;

  const isPendingZero = selfPending === 0;
  const isMissingZero = Array.isArray(selfMissing);

  const getCalendarMedianTime = (calendarData: CalendarDay[]) => {
    const times = calendarData
      .map(({ checkinTime }) => checkinTime)
      .filter((checkinTime): checkinTime is string => Boolean(checkinTime));

    return getMedianTime(times);
  };

  const endDate = getFormattedDate();
  const startDate = getFormattedDate(currentFiscalYear.startDate);
  const shouldFetchSummary = !isObjectEmpty(currentFiscalYear);

  const selfSummaryFilter = useMemo(
    () => ({
      fetchType: SELF,
      startDate,
      endDate,
      fields: 'worklog,missing',
    }),
    [endDate, startDate]
  );

  const {
    data: selfSummaryResponse,
    isLoading: isSelfAttendanceLoading,
    isError: isSelfSummaryError,
    error: selfSummaryError,
    errorUpdatedAt: selfSummaryErrorUpdatedAt,
  } = useFetchAttendanceSummary(selfSummaryFilter, shouldFetchSummary);

  useEffect(() => {
    if (!isSelfSummaryError || !selfSummaryError || selfSummaryErrorUpdatedAt <= lastHandledSummaryErrorAtRef.current) {
      return;
    }

    handleError(selfSummaryError);
    lastHandledSummaryErrorAtRef.current = selfSummaryErrorUpdatedAt;
  }, [isSelfSummaryError, selfSummaryError, selfSummaryErrorUpdatedAt]);

  const currentDate = getFormattedDate();
  const calendarDataSize = 40;
  const [weekStartDate, weekEndDate] = getCurrentWeekRange(currentDate);
  const medianStartDate = subtractDays(currentDate, N_OF_MEDIAN_TIME);

  const calendarFilter = useMemo(
    () => ({
      fetchType: SELF,
      startDate: medianStartDate,
      endDate: weekEndDate,
      withCheckinTime: true,
      size: calendarDataSize,
    }),
    [medianStartDate, weekEndDate]
  );

  const { data: calendarData, isLoading: isCalendarDataLoading } = useFetchAttendanceCalendarQuery<CalendarDay[]>(
    calendarFilter,
    Boolean(user?.id)
  );

  const { data: unfreezeRequestsData = [] } = useFetchUnfreezeRequestsQuery<UnfreezeRequest[]>(
    user?.id,
    Boolean(user?.id)
  );

  useEffect(() => {
    if (!selfSummaryResponse?.data) {
      return;
    }

    const selfData = selfSummaryResponse.data as { missing?: number; worklog?: { pending?: number } };
    setSelfAttendance({ missing: selfData.missing || 0, pending: selfData.worklog?.pending || 0 });
  }, [selfSummaryResponse, setSelfAttendance]);

  const { unfreezeRequests, clockinHistory, totalDuration, medianTime, workStatusSummaryData } = useMemo(() => {
    const emptySummary: WorkStatusSummary = { wfh: 0, wfo: 0, leave: 0 };

    if (!calendarData) {
      return {
        unfreezeRequests: unfreezeRequestsData,
        clockinHistory: [] as CalendarDay[],
        totalDuration: 0,
        medianTime: null as string | null,
        workStatusSummaryData: emptySummary,
      };
    }

    const weeklyData = filterDataByDateRange({
      data: calendarData,
      key: 'date',
      startDate: weekStartDate,
      endDate: weekEndDate,
    }) as CalendarDay[];
    const medianData = filterDataByDateRange({
      data: calendarData,
      key: 'date',
      startDate: medianStartDate,
      endDate: currentDate,
    }) as CalendarDay[];

    const medianTimeValue = getCalendarMedianTime(medianData);
    const weeklyTotalDuration = getWorklogTotalDuration(weeklyData);
    const unfreezeRequestsSummaryData = unfreezeRequestsData.filter(
      (request): request is UnfreezeRequestSummaryInput =>
        Boolean(request.unfreezeFrom && request.unfreezeTo && request.updatedAt) &&
        typeof request.isUnfrozen === 'boolean'
    );

    return {
      unfreezeRequests: unfreezeRequestsData,
      clockinHistory: medianData,
      totalDuration: weeklyTotalDuration,
      medianTime: medianTimeValue ? convertTimeToAMPM(medianTimeValue) : null,
      workStatusSummaryData: getWorkStatusSummaryData(medianData, unfreezeRequestsSummaryData),
    };
  }, [calendarData, unfreezeRequestsData, weekStartDate, weekEndDate, medianStartDate, currentDate]);

  const WORK_STATUS_MAPPING: Record<keyof WorkStatusSummary, string> = {
    wfo: WORK_STATUS.WFO,
    wfh: REMOTE,
    leave: WORK_STATUS.LEAVE,
  };

  const isLoading = isCalendarDataLoading || isSelfAttendanceLoading;

  if (isLoading) {
    return (
      <>
        <SkeletonTheme baseColor="#fdfdfd" highlightColor="#f0f3f5">
          <Skeleton className="weekly-record-card-loader wp-100" height={62} />
          {shouldShowWorkshift && <Skeleton className="median-office-time-card-loader wp-100" height={86} />}
        </SkeletonTheme>
      </>
    );
  }

  return (
    <>
      <div className="d-flex actions flex-direction-column">
        <div className="weekly-action d-flex">
          <div className="actions__container">
            <div className="actions__footer justify-content-center">
              <div className="footer__log-content">
                <div className="footer__top">{totalDuration}</div>
                <div>
                  <p className="footer__bottom-text">logged this week</p>
                </div>
              </div>
            </div>
          </div>
          <div className="pending-action">
            <a
              className={classNames(
                'pending-action__item',
                { 'pending-action__item--empty': isPendingZero },
                { 'pending-action__item--pending': !isPendingZero }
              )}
              href={ATTENDANCE_URL}
              target={'_blank'}
              onClick={() => {
                mixpanel.trackEvent(self.worklogPending);
              }}
              rel="noreferrer"
            >
              {selfPending}
              <div className="pending-action__title">Worklog Pending</div>
            </a>
            <a
              className={classNames(
                'pending-action__item',
                { 'pending-action__item--empty': isMissingZero },
                { 'pending-action__item--missed': !isMissingZero }
              )}
              href={ATTENDANCE_URL}
              target="_blank"
              onClick={() => {
                mixpanel.trackEvent(self.attendanceMissed);
              }}
              rel="noreferrer"
            >
              <div>{isMissingZero ? 0 : selfMissing}</div>
              <div className="pending-action__title">Attendance Missed</div>
            </a>
          </div>
        </div>
      </div>

      {shouldShowWorkshift && (
        <div className="actions median-office-time px-16 pbpx-12">
          <ClockInHistoryModal
            TriggerBtn={() => <ClockInHistoryButton setIsHistoryModalOpen={setIsHistoryModalOpen} />}
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            clockinHistoryData={clockinHistory}
            isClockinHistoryLoading={isCalendarDataLoading}
            medianClockInTime={medianTime}
            unfreezeRequests={unfreezeRequests}
          />
          <div className="median-office-time__main d-flex justify-content-between align-items-center">
            <ClockInTime medianClockInTime={medianTime} />

            {(Object.entries(workStatusSummaryData) as Array<[keyof WorkStatusSummary, number]>).map(
              ([status, count], index) => {
                const workStatus = WORK_STATUS_MAPPING[status];
                const isLastChild = index === Object.entries(workStatusSummaryData).length - 1;

                return (
                  <WorkStatusCount
                    key={status}
                    status={workStatus}
                    count={count}
                    className={!isLastChild ? 'border-right-grey-15' : ''}
                  />
                );
              }
            )}
          </div>
        </div>
      )}
    </>
  );
}
